<template>
  <BaseDialog
    type="basic"
    title="Profil als PDF downloaden"
    :is-open="isOpen"
    class="w-screen md:w-[600px]"
    @close="emits('close')"
  >
    <LoadingWrapper :state="status">
      <p class="text-body-medium text-on-surface-variant mb-2">
        Wähle die Themen aus, die du exportieren möchtest.
      </p>
      <ul class="flex w-full flex-col gap-5 pb-10">
        <li v-for="topic in exportOptions.feedbackTopics" :key="topic.id">
          <BaseCheckboxListItem
            :disabled="
              topic.type === FeedbackTopicType.Evaluation &&
              topic.surveys &&
              topic.surveys.length === 0
            "
            :title="topic.title"
            :description="
              topic.type === FeedbackTopicType.Evaluation &&
              topic.surveys &&
              topic.surveys.length > 0
                ? topic.surveys.length + ' ' + $t('common.survey', { count: topic.surveys.length })
                : undefined
            "
            :selected="isTopicSelected(topic.id)"
            :icon="topic.icon"
            :class="[
              isTopicSelected(topic.id) &&
                topic.type === FeedbackTopicType.Evaluation &&
                topic.surveys &&
                topic.surveys.length > 0 &&
                'rounded-b-extra-small!',
            ]"
            @click="
              () => {
                if (
                  topic.type === FeedbackTopicType.Evaluation &&
                  topic.surveys &&
                  topic.surveys.length === 0
                ) {
                  return
                }
                toggleTopic(topic.id)
              }
            "
          />
          <BaseAnimatedExtend
            :duration="200"
            :is-extended="
              topic.type === FeedbackTopicType.Evaluation &&
              isTopicSelected(topic.id) &&
              topic.surveys &&
              topic.surveys.length > 0
            "
          >
            <ul
              v-if="
                topic.type === FeedbackTopicType.Evaluation &&
                topic.surveys &&
                topic.surveys.length > 0
              "
              class="flex flex-col gap-0.5 pt-0.5"
            >
              <li
                v-for="survey in topic.surveys"
                :key="survey.id"
                class="*:rounded-extra-small! last:*:rounded-b-large!"
              >
                <SelectableSurveyItem
                  compact
                  :survey="survey"
                  :is-selected="isSurveySelected(topic.id, survey.id)"
                  @click="toggleSurvey(topic.id, survey.id)"
                />
              </li>
            </ul>
          </BaseAnimatedExtend>
        </li>
      </ul>
    </LoadingWrapper>

    <template #dismiss>
      <BaseTextButton color="text" size="M" @click="emits('close')">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton
        color="filled"
        :disabled="payload.topics.length === 0"
        @click="submit"
        icon="download"
        size="M"
      >
        {{ $t('common.download') }}
      </BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { FeedbackTopicType } from '@/core/models/feedback-topic'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import BaseCheckboxListItem from '@/core/components/base/list-item/BaseCheckboxListItem.vue'
  import BaseAnimatedExtend from '@/core/components/base/animated-extend/BaseAnimatedExtend.vue'
  import SelectableSurveyItem from '@/core/components/survey-selector/SelectableSurveyItem.vue'
  import {
    useCoacheeProfileExportOptions,
    type CoacheeProfilePdfPayload,
  } from '../composables/use-coachee-profile-export-options'

  interface Props {
    isOpen: boolean
  }

  defineProps<Props>()

  const emits = defineEmits<{
    (e: 'close'): void
    (e: 'submit', payload: CoacheeProfilePdfPayload): void
  }>()

  const {
    exportOptions,
    status,
    payload,
    toggleTopic,
    isSurveySelected,
    toggleSurvey,
    isTopicSelected,
  } = useCoacheeProfileExportOptions()

  const submit = () => {
    emits('submit', payload.value)
    emits('close')
  }
</script>
